import * as React from 'react';

import { useQueryClient, MutationStatus } from '@tanstack/react-query';

import useGetCabinEventReport from '../api/hooks/cabin-event/useGetCabinEventReport';
import useUpdateCabinEvent from '../api/hooks/cabin-event/useUpdateCabinEvent';
import useSubmitCabinEvent from '../api/hooks/cabin-event/useSubmitCabinEvent';
import useApproveCabinEvent from '../api/hooks/cabin-event/useApproveCabinEvent';
import useGetAllowedActions from '../api/hooks/common/useGetAllowedActions';
import queryKeys from '../api/queryKeys';

import {
  AllowedActions,
  AuthorizationRequestType,
  CabinEventReport,
  CabinEventReportDTO,
  ReportType,
} from '../api/clients';

import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorOverlay from '../../components/ErrorOverlay';
import useTitle from '../api/hooks/common/useTitle';

type ICabinEventReportData = {
  cabinEventReport: CabinEventReport;
  allowedActions: AllowedActions | undefined;

  update: (data: CabinEventReport, successCallback?: any, failCallback?: any) => void;
  updateStatus: MutationStatus;

  submit: (eventId: number | undefined, successCallback?: any, failCallback?: any) => void;
  submitStatus: MutationStatus;

  approve: (eventId: number | undefined, successCallback?: any, failCallback?: any) => void;
  approveStatus: MutationStatus;
};

const CabinEventReportDataContext = React.createContext<ICabinEventReportData>(undefined!);

export function CabinEventReportDataProvider({
  children,
  cabinEventReportId,
  role,
}: {
  children: React.ReactNode,
  cabinEventReportId: number,
  role?: AuthorizationRequestType,
}) {
  const queryClient = useQueryClient();
  useTitle(document.documentURI);
  const {
    data: reportData,
    status: reportStatus,
    error: reportError,
  } = useGetCabinEventReport(cabinEventReportId, role || AuthorizationRequestType.Contribute);

  const { data: allowedActions } = useGetAllowedActions(
    reportData?.employeeId,
    reportData?.status,
    ReportType.CabinEventReport,
  );

  const { status: updateMutationStatus, mutate: updateMutation } = useUpdateCabinEvent();
  const { status: submitMutationStatus, mutate: submitMutation } = useSubmitCabinEvent();
  const { status: approveMutationStatus, mutate: approveMutation } = useApproveCabinEvent();

  const onSuccessfulMutation = (
    returnData: CabinEventReportDTO,
    eventId: number | undefined,
    successCallback?: any,
    failCallback?: any,
  ) => {
    const { eventReport, error } = returnData;

    queryClient.setQueryData([queryKeys.CabinEvent.Get, { id: eventId }], eventReport);
    if (!error) {
      if (successCallback) successCallback(eventReport);
    } else if (failCallback) failCallback(error);
  };

  const cabinEventReportData = React.useMemo<ICabinEventReportData>(() => ({
    cabinEventReport: reportData!,
    allowedActions: allowedActions!,

    updateStatus: updateMutationStatus,
    update: (data: CabinEventReport, successCallback?: any, failCallback?: any) => {
      updateMutation(data, {
        onSuccess: (returnData) => {
          const { eventReport, error } = returnData;
          queryClient.setQueryData(['CabinEvent.GetCabinEventReportById', { id: data.id }], eventReport);
          if (!error) {
            if (successCallback) successCallback();
          } else if (failCallback) failCallback(error);
        },
      });
    },

    approveStatus: approveMutationStatus,
    approve: (eventId: number | undefined, successCallback?: any, failCallback?: any) => {
      if (eventId !== undefined) {
        approveMutation(eventId, {
          onSuccess: (returnData) => onSuccessfulMutation(
            returnData,
            eventId,
            successCallback,
            failCallback,
          ),
        });
      }
    },

    submitStatus: submitMutationStatus,
    submit: (eventId: number | undefined, successCallback?: any, failCallback?: any) => {
      if (eventId !== undefined) {
        submitMutation(eventId, {
          onSuccess: (returnData) => onSuccessfulMutation(
            returnData,
            eventId,
            successCallback,
            failCallback,
          ),
        });
      }
    },
  }), [
    reportData,
    allowedActions,
    updateMutationStatus,
    approveMutationStatus,
    submitMutationStatus,
  ]);

  if (reportStatus === 'pending') {
    return (
      <LoadingOverlay />
    );
  }

  if (reportStatus === 'error') {
    return (
      <ErrorOverlay
        title={reportError?.title}
        message={`An error occurred retrieving data for form: ${cabinEventReportId}`}
      />
    );
  }

  if (reportStatus === 'success' && reportData !== undefined) {
    return (
      <CabinEventReportDataContext.Provider value={cabinEventReportData}>
        {children}
      </CabinEventReportDataContext.Provider>
    );
  }

  return null;
}

export const useCabinEventReportData = () => React.useContext(CabinEventReportDataContext);
